import axios from 'axios';
import {
  Caption, Col, Heading, Modal, Row,
} from '@elevationscu/ui-component-lib';
import {
  Fragment, useState, forwardRef, useImperativeHandle,
} from 'react';
import { formatNumber, formatPercentage } from './utils';

import styles from './AmortizationTable.module.scss';

const AmortizationTable = forwardRef(({
  loanAmount, interestRate, termMonths, amortization, propertyValue, estimateTipmi, propertyTaxes, hazardInsurance, pmi,
}, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [calcResults, setCalcResults] = useState();
  const [displayLoanAmount, setDisplayLoanAmount] = useState(loanAmount);
  const [displayInterestRate, setDisplayInterestRate] = useState(interestRate);
  const [displayTermMonths, setDisplayTermMonths] = useState(termMonths);

  const handleClose = () => {
    setShowModal(false);
  };

  const handleSubmitValid = async (cardLoanAmount, cardInterestRate, cardTermMonths) => {
    const resolvedLoanAmount = cardLoanAmount ?? loanAmount;
    const resolvedInterestRate = cardInterestRate ?? interestRate;
    const resolvedTermMonths = cardTermMonths ?? termMonths;
    setDisplayLoanAmount(resolvedLoanAmount);
    setDisplayInterestRate(resolvedInterestRate);
    setDisplayTermMonths(resolvedTermMonths);
    try {
      const url = `${process.env.REACT_APP_MCCC_API_URL}/refi/amortization`;

      const payload = {
        language: 'en',
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        loanAmount: resolvedLoanAmount,
        interestRate: formatPercentage(resolvedInterestRate),
        termMonths: resolvedTermMonths,
        amortization,
        propertyValue,
        estimateTipmi,
        propertyTaxes,
        hazardInsurance,
        pmi,
      };

      const response = await axios.post(url, payload);

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`);
      }

      setCalcResults(response.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useImperativeHandle(ref, () => ({
    open: (cardLoanAmount, cardInterestRate, cardTermMonths) => {
      handleSubmitValid(cardLoanAmount, cardInterestRate, cardTermMonths);
      setShowModal(true);
    },
  }));

  return (
    <Modal className={styles.modal} open={showModal} onDismiss={handleClose} name="prospect-complete-modal" contentOverrideClassName={styles.contentOverrideClassName} closeButtonOnTop data-test-id="amortization_table_modal">
      <div className={styles.modalInnerWrapper}>
        <div className={styles.boxContent}>
          {calcResults && (
            <div className={styles.Results}>
              <Heading className={styles.ResultsHeading} element="h3" size={3}>
                Amortization Schedule
              </Heading>
              <br />
              <table summary="Summary Table" className={styles.ResultsTable} role="grid">
                <caption>
                  Summary table
                </caption>
                <tbody>
                  <tr>
                    <th colSpan="2" className="section">Loan information</th>
                  </tr>
                  <tr>
                    <th scope="row">
                      Loan Amount
                    </th>
                    <td data-test-id="amortization-table-loan-amount-value">
                      $
                      {formatNumber(displayLoanAmount)}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      Annual interest rate
                    </th>
                    <td data-test-id="amortization-table-interest-rate-value">
                      {displayInterestRate}
                      %
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      Number of months
                    </th>
                    <td data-test-id="amortization-table-term-in-months-value">{displayTermMonths}</td>
                  </tr>
                  <tr>
                    <th scope="row">
                      Monthly principal and interest payment
                    </th>
                    <td data-test-id="amortization-table-monthly-and-interest-value">{calcResults.pandi}</td>
                  </tr>
                  <tr>
                    <th scope="row">
                      Total interest
                    </th>
                    <td data-test-id="amortization-table-total-interest">{`$${formatNumber(calcResults.totalInterest)}`}</td>
                  </tr>
                </tbody>
              </table>
              <br />

              <Heading className={styles.ResultsHeading} element="h3" size={3}>
                Graph
              </Heading>
              <br />

              <Row>
                <Col className={styles.graphCol}>
                  <img
                    src={calcResults.chartUrl}
                    alt="Graph of results"
                  />
                </Col>
              </Row>
              <br />

              {calcResults.year && calcResults.year.length > 0 && (
              <Fragment>
                <Heading className={styles.ResultsHeading} element="h3" size={3}>
                  Amortization Table
                </Heading>
                <br />

                <table
                  summary="Amortization Table"
                  className={styles.ResultsTable}
                  role="grid"
                >
                  <caption>Amortization Table</caption>

                  <tbody>
                    <tr>
                      <th>
                        {amortization === '2' ? 'Year' : 'Month'}
                      </th>
                      <th>
                        Beginning Balance
                      </th>
                      <th>
                        Principal
                      </th>
                      <th>
                        Interest
                      </th>
                      <th>
                        Payment
                      </th>
                      <th>
                        Ending Balance
                      </th>
                    </tr>

                    {calcResults.year.map((year) => (
                      <tr key={year.period}>
                        <th scope="row">{year.period}</th>
                        <td>{year.beginningBalance}</td>
                        <td>{year.principal}</td>
                        <td>{year.interest}</td>
                        <td>{year.payment}</td>
                        <td>{year.endingBalance}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Fragment>
              )}
              <br />
              <Caption>
                This information may help you analyze your financial needs. It is based on information
                and assumptions provided by you regarding your goals, expectations and financial
                situation. The calculations do not infer that the company assumes any fiduciary duties.
                The calculations provided should not be construed as financial, legal or tax advice. In
                addition, such information should not be relied upon as the only source of information.
                This information is supplied from sources we believe to be reliable but we cannot
                guarantee its accuracy. Hypothetical illustrations may provide historical or current
                performance information. Past performance does not guarantee nor indicate future
                results.
              </Caption>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
});

export { AmortizationTable };
